import { Box, Button, Icon, InlineStack, Panel, PanelSection, Text } from '@customerio/pluma-components/react';
import { useCallback, useMemo, useState } from 'react';
import type { CopilotSession, CopilotStatefulToolCall } from '@customerio/hydra-copilot';
import { useAnalytics } from '@/services/analytics';
import { isEmptyString } from '@/utils/string';
import { capabilityStatuses } from '../../utils/capability-status';
import { humanizeScope, parseScope } from '../../utils/scope-display';

type CardState = 'pending' | 'submitting' | 'confirmed' | 'denied' | 'failed';

interface ImpactApprovalCardProps {
  toolCall: CopilotStatefulToolCall;
  session: Pick<CopilotSession, 'sendMessage'>;
}

function initialCardState(toolCall: CopilotStatefulToolCall): CardState {
  for (const status of capabilityStatuses(toolCall.response)) {
    if (status === 'approved' || status === 'partial') return 'confirmed';
    if (status === 'denied' || status === 'rejected') return 'denied';
  }
  return 'pending';
}

export function ImpactApprovalCard({ toolCall, session }: ImpactApprovalCardProps) {
  const [state, setState] = useState<CardState>(() => initialCardState(toolCall));
  const { track } = useAnalytics();

  // Reject legacy payload shapes — a restored session shouldn't throw inside this render.
  const rawApproval = toolCall.approval_request;
  const approval =
    rawApproval != null && rawApproval.kind === 'capability' && Array.isArray(rawApproval.scopes) ? rawApproval : null;

  const sendDecision = useCallback(
    async (result: Record<string, unknown>, settled: CardState) => {
      setState('submitting');
      try {
        await session.sendMessage([
          {
            role: 'user',
            type: 'tool_response',
            tool_responses: [
              {
                tool_call_id: toolCall.id,
                name: toolCall.name,
                response: { result, error: null },
                finished: true,
              },
            ],
            timestamp: Math.floor(Date.now() / 1000),
          },
        ]);
        setState(settled);
      } catch {
        setState('failed');
      }
    },
    [session, toolCall.id, toolCall.name],
  );

  // Approving in bulk is only informed if the card says which of these the agent wrote itself.
  // Validated like scopes above: a malformed payload must not throw inside this render,
  // which sits outside the tool-call error boundary.
  const agentAuthored = useMemo(() => {
    const raw = approval?.agent_authored_scopes;
    return new Set(Array.isArray(raw) ? raw : []);
  }, [approval]);

  // Counted from the rows actually tagged, not from the backend array, so the summary can
  // never claim more than the card shows.
  const authoredCount = useMemo(
    () => (approval?.scopes ?? []).filter((scope) => agentAuthored.has(scope)).length,
    [approval, agentAuthored],
  );

  const onConfirm = useCallback(() => {
    if (approval == null) return;
    track('aiAssistant', 'capabilityApprovalConfirmed', {
      scopes: approval.scopes.length,
    });
    void sendDecision({ status: 'approved', scopes: approval.scopes }, 'confirmed');
  }, [approval, sendDecision, track]);

  const onDeny = useCallback(() => {
    track('aiAssistant', 'capabilityApprovalDenied', {
      scopes: approval?.scopes.length ?? 0,
    });
    void sendDecision({ status: 'rejected', user_initiated: true }, 'denied');
  }, [approval, sendDecision, track]);

  if (approval == null) return null;

  // Only 'failed' renders its own UI; settled states unmount via the parent segment.
  if (state === 'failed') {
    return (
      <Panel>
        <PanelSection>
          <Box display="flex" flexDirection="column" gap="200">
            <InlineStack alignBlock="center" gap="075">
              <Icon name="critical" color="critical" size="sm" />
              <Text size="xs" color="critical">
                Couldn't deliver your decision. Check your connection and try again.
              </Text>
            </InlineStack>
            <InlineStack alignBlock="center" gap="075" wrap>
              <Button size="sm" variant="tertiary" onClick={() => setState('pending')}>
                Retry
              </Button>
            </InlineStack>
          </Box>
        </PanelSection>
      </Panel>
    );
  }

  return (
    <Panel>
      <PanelSection>
        <Box display="flex" flexDirection="column" gap="200">
          <Box display="flex" flexDirection="column" gap="050">
            <Text size="sm" weight="medium">
              The Agent is requesting permission
            </Text>
            {approval.reason != null && approval.reason !== '' ? (
              <Text size="xs" color="secondary">
                {approval.reason}
              </Text>
            ) : null}
          </Box>

          {approval.scopes.length > 0 ? (
            <Box display="flex" flexDirection="column" gap="050">
              {approval.scopes.map((scope, index) => (
                <InlineStack key={`${index}-${scope}`} alignBlock="center" gap="050">
                  <Icon name={iconForScope(scope)} color="secondary" size="sm" />
                  <Text size="xs" color="secondary">
                    {humanizeScopeOrRaw(scope)}
                  </Text>
                  {agentAuthored.has(scope) ? (
                    <Text size="xs" color="caution">
                      · written by the Agent
                    </Text>
                  ) : null}
                </InlineStack>
              ))}
            </Box>
          ) : null}

          {authoredCount > 0 ? (
            <InlineStack alignBlock="center" gap="050">
              <Icon name="caution" color="caution" size="sm" />
              <Text size="xs" color="caution">
                {provenanceSummary(authoredCount, approval.scopes.length)}
              </Text>
            </InlineStack>
          ) : null}

          {!isEmptyString(approval.auto_defer_reason) ? (
            <InlineStack alignBlock="center" gap="050">
              <Icon name="sparkle" color="secondary" size="sm" />
              <Text size="xs" color="secondary">
                {approval.auto_defer_reason}
              </Text>
            </InlineStack>
          ) : null}

          <InlineStack alignBlock="center" gap="075" wrap>
            <Button size="sm" variant="tertiary" onClick={onDeny} isDisabled={state !== 'pending'}>
              Deny
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={onConfirm}
              isDisabled={state !== 'pending'}
              isLoading={state === 'submitting'}
            >
              Approve
            </Button>
          </InlineStack>
        </Box>
      </PanelSection>
    </Panel>
  );
}

function iconForScope(scope: string): 'campaigns' | 'email' | 'segment' | 'design-studio' | 'broadcast' {
  const parts = parseScope(scope);
  switch (parts?.resource) {
    case 'campaign':
      return 'campaigns';
    case 'newsletter':
    case 'transactional':
      return 'email';
    case 'segment':
      return 'segment';
    case 'template':
      return 'design-studio';
    case 'in_app_broadcast':
    case 'broadcast':
      return 'broadcast';
    default:
      return 'campaigns';
  }
}

function provenanceSummary(authored: number, total: number): string {
  if (authored === total && total === 1) {
    return 'The Agent wrote this content in this session. Approving publishes it unreviewed.';
  }
  const subject = authored === total ? 'All of these are' : `${authored} of these ${total} are`;
  return `${subject} content the Agent wrote in this session. Approving publishes it unreviewed.`;
}

// Fall back to the raw scope when parsing fails so the card still renders.
function humanizeScopeOrRaw(scope: string): string {
  return humanizeScope(scope) ?? scope;
}
